import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CreditCard, Wallet, Shield, Check, MapPin, Calendar } from 'lucide-react'
import { useCartStore, useCurrencyStore, useLanguageStore, useAuthStore } from '../../lib/store'
import { formatPrice, daysBetween } from '../../lib/utils'
import { bookingsApi } from '../../lib/api'

export default function CheckoutPage() {
    useTranslation()
    const navigate = useNavigate()
    const { items, clearCart } = useCartStore()
    const { currency } = useCurrencyStore()
    const { language } = useLanguageStore()
    const { user, isAuthenticated } = useAuthStore()

    const [paymentMethod, setPaymentMethod] = useState<'card' | 'wallet' | 'cash'>('card')
    const [agreeTerms, setAgreeTerms] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const [customerInfo, setCustomerInfo] = useState({
        fullName: user?.fullName || '',
        email: user?.email || '',
        phone: user?.phone || '',
    })

    if (!isAuthenticated) {
        navigate('/login?redirect=/checkout')
        return null
    }

    if (items.length === 0) {
        navigate('/vehicles')
        return null
    }

    const calculateTotal = () => {
        return items.reduce((total, item) => {
            const days = daysBetween(item.startDate, item.endDate)
            const vehicleTotal = item.vehicle.dailyRate * days
            const addonsTotal = item.addons.reduce((sum, addon) => {
                const addonData = item.vehicle.addons?.find((a) => a.id === addon.addonId)
                return sum + (addonData?.price || 0) * days
            }, 0)
            return total + vehicleTotal + addonsTotal
        }, 0)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!agreeTerms) {
            setError(language === 'en' ? 'Please agree to terms and conditions' : 'Vui lòng đồng ý với điều khoản')
            return
        }

        setLoading(true)
        setError('')

        try {
            // Create booking for each item
            for (const item of items) {
                const response = await bookingsApi.create({
                    vehicleId: item.vehicle.id,
                    startDate: item.startDate.toISOString(),
                    endDate: item.endDate.toISOString(),
                    pickupLocation: item.pickupLocation || '',
                    addons: item.addons.map((a) => a.addonId),
                })

                if (response.error) {
                    throw new Error(response.error)
                }
            }

            clearCart()
            navigate('/dashboard/bookings?success=true')
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Booking failed')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="container mx-auto px-4">
                <h1 className="text-3xl font-bold mb-8">{language === 'en' ? 'Checkout' : 'Thanh Toán'}</h1>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Customer Info */}
                        <div className="card">
                            <h2 className="text-xl font-bold mb-4">{language === 'en' ? 'Customer Information' : 'Thông Tin Khách Hàng'}</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">{language === 'en' ? 'Full Name' : 'Họ tên'} *</label>
                                    <input
                                        type="text"
                                        required
                                        value={customerInfo.fullName}
                                        onChange={(e) => setCustomerInfo({ ...customerInfo, fullName: e.target.value })}
                                        className="input"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Email *</label>
                                    <input
                                        type="email"
                                        required
                                        value={customerInfo.email}
                                        onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                                        className="input"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium mb-1">{language === 'en' ? 'Phone' : 'Số điện thoại'} *</label>
                                    <input
                                        type="tel"
                                        required
                                        value={customerInfo.phone}
                                        onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                                        className="input"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Booking Summary */}
                        <div className="card">
                            <h2 className="text-xl font-bold mb-4">{language === 'en' ? 'Booking Summary' : 'Tóm Tắt Đặt Xe'}</h2>
                            <div className="space-y-4">
                                {items.map((item) => {
                                    const days = daysBetween(item.startDate, item.endDate)
                                    const translation = item.vehicle.translations?.[language] || { title: item.vehicle.brand + ' ' + item.vehicle.model }

                                    return (
                                        <div key={item.vehicle.id} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                                            <div className="w-24 h-20 bg-gray-200 rounded-lg flex items-center justify-center">
                                                <span className="text-2xl">🏍️</span>
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-semibold">{translation.title}</h3>
                                                <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                                                    <span className="flex items-center">
                                                        <Calendar className="w-4 h-4 mr-1" />
                                                        {days} {language === 'en' ? 'days' : 'ngày'}
                                                    </span>
                                                    {item.pickupLocation && (
                                                        <span className="flex items-center">
                                                            <MapPin className="w-4 h-4 mr-1" />
                                                            {item.pickupLocation}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-bold text-primary-600">
                                                    {formatPrice(item.vehicle.dailyRate * days, currency)}
                                                </p>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>

                        {/* Payment Method */}
                        <div className="card">
                            <h2 className="text-xl font-bold mb-4">{language === 'en' ? 'Payment Method' : 'Phương Thức Thanh Toán'}</h2>
                            <div className="space-y-3">
                                <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition ${paymentMethod === 'card' ? 'border-primary-500 bg-primary-50' : 'hover:bg-gray-50'}`}>
                                    <input
                                        type="radio"
                                        name="payment"
                                        value="card"
                                        checked={paymentMethod === 'card'}
                                        onChange={() => setPaymentMethod('card')}
                                        className="mr-3"
                                    />
                                    <CreditCard className="w-6 h-6 mr-3 text-gray-600" />
                                    <div>
                                        <p className="font-medium">{language === 'en' ? 'Credit/Debit Card' : 'Thẻ tín dụng/ghi nợ'}</p>
                                        <p className="text-sm text-gray-500">Visa, Mastercard, JCB</p>
                                    </div>
                                </label>

                                <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition ${paymentMethod === 'wallet' ? 'border-primary-500 bg-primary-50' : 'hover:bg-gray-50'}`}>
                                    <input
                                        type="radio"
                                        name="payment"
                                        value="wallet"
                                        checked={paymentMethod === 'wallet'}
                                        onChange={() => setPaymentMethod('wallet')}
                                        className="mr-3"
                                    />
                                    <Wallet className="w-6 h-6 mr-3 text-gray-600" />
                                    <div>
                                        <p className="font-medium">{language === 'en' ? 'Wallet Balance' : 'Số dư ví'}</p>
                                        <p className="text-sm text-gray-500">{language === 'en' ? 'Pay from your wallet' : 'Thanh toán từ ví của bạn'}</p>
                                    </div>
                                </label>

                                <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition ${paymentMethod === 'cash' ? 'border-primary-500 bg-primary-50' : 'hover:bg-gray-50'}`}>
                                    <input
                                        type="radio"
                                        name="payment"
                                        value="cash"
                                        checked={paymentMethod === 'cash'}
                                        onChange={() => setPaymentMethod('cash')}
                                        className="mr-3"
                                    />
                                    <span className="text-2xl mr-3">💵</span>
                                    <div>
                                        <p className="font-medium">{language === 'en' ? 'Cash on Pickup' : 'Tiền mặt khi nhận xe'}</p>
                                        <p className="text-sm text-gray-500">{language === 'en' ? 'Pay when you receive the vehicle' : 'Thanh toán khi nhận xe'}</p>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Order Summary Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="card sticky top-20">
                            <h2 className="text-xl font-bold mb-4">{language === 'en' ? 'Order Summary' : 'Tổng Đơn Hàng'}</h2>

                            <div className="space-y-3 mb-4">
                                {items.map((item) => {
                                    const days = daysBetween(item.startDate, item.endDate)
                                    return (
                                        <div key={item.vehicle.id} className="flex justify-between text-sm">
                                            <span>{item.vehicle.brand} {item.vehicle.model} x {days} {language === 'en' ? 'days' : 'ngày'}</span>
                                            <span>{formatPrice(item.vehicle.dailyRate * days, currency)}</span>
                                        </div>
                                    )
                                })}
                            </div>

                            <div className="border-t pt-4 mb-4">
                                <div className="flex justify-between text-lg font-bold">
                                    <span>{language === 'en' ? 'Total' : 'Tổng cộng'}</span>
                                    <span className="text-primary-600">
                                        {formatPrice(calculateTotal(), currency)}
                                    </span>
                                </div>
                            </div>

                            {/* Escrow Notice */}
                            <div className="bg-blue-50 rounded-lg p-3 mb-4 text-sm text-blue-700">
                                <div className="flex items-start gap-2">
                                    <Shield className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-semibold">{language === 'en' ? 'Secure Payment' : 'Thanh toán an toàn'}</p>
                                        <p className="text-xs mt-1">
                                            {language === 'en'
                                                ? 'Your payment is protected. Funds are held securely until rental completion.'
                                                : 'Tiền của bạn được bảo vệ. Chỉ chuyển cho chủ xe sau khi hoàn thành thuê.'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Terms */}
                            <label className="flex items-start gap-2 mb-4">
                                <input
                                    type="checkbox"
                                    checked={agreeTerms}
                                    onChange={(e) => setAgreeTerms(e.target.checked)}
                                    className="mt-1"
                                />
                                <span className="text-sm text-gray-600">
                                    {language === 'en'
                                        ? 'I agree to the Terms of Service and Cancellation Policy'
                                        : 'Tôi đồng ý với Điều khoản dịch vụ và Chính sách hủy'}
                                </span>
                            </label>

                            {error && (
                                <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
                                    {error}
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                onClick={handleSubmit}
                                disabled={loading || !agreeTerms}
                                className="w-full py-4 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white rounded-lg font-bold text-lg transition flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <span>{language === 'en' ? 'Processing...' : 'Đang xử lý...'}</span>
                                ) : (
                                    <>
                                        <Check className="w-5 h-5" />
                                        {language === 'en' ? 'Confirm & Pay' : 'Xác Nhận & Thanh Toán'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
